import { MenuListData, CateMenuListData } from "./model";

export type MenuRow = { key: string; menus: MenuListData[] };
export type MenuSection = { cateId: number; cateName: string; alcohol?: boolean; data: MenuRow[] };

export function columnsFor(width: number, design: "LIST" | "CARDS"): number {
  if (design === "CARDS") {
    if (width < 700) return 2;
    if (width < 1100) return 3;
    return 4;
  }
  if (width < 700) return 1;
  if (width < 1100) return 2;
  return 3;
}

export function chunkIntoRows(menus: MenuListData[], cols: number, cateId: number): MenuRow[] {
  const rows: MenuRow[] = [];
  for (let i = 0; i < menus.length; i += cols) {
    rows.push({ key: `${cateId}-r${rows.length}`, menus: menus.slice(i, i + cols) });
  }
  return rows;
}

export function buildSections(cateMenus: CateMenuListData[], cols: number): MenuSection[] {
  return cateMenus.map((c) => ({
    cateId: c.cateId,
    cateName: c.cateName,
    alcohol: c.alcohol,
    data: chunkIntoRows(c.menus, cols, c.cateId),
  }));
}

export type FlatEntry =
  | { type: "header"; key: string; sectionIndex: number; cateName: string }
  | { type: "row"; key: string; sectionIndex: number; row: MenuRow };

export type FlatLayout = {
  entries: FlatEntry[];
  offsets: number[]; // offsets[i] = pixel offset of entries[i] (prefix sum)
  sectionOffset: number[]; // sectionOffset[s] = pixel offset of section s's header
  totalHeight: number;
};

// Flatten sections into a single 1:1 array: for each section push one header
// (height headerH) then its rows (height rowH). Because the resulting FlatList
// index == array index (no hidden footer slot like SectionList), offsets is an
// exact prefix sum and getItemLayout is trivially correct.
export function flattenWithOffsets(
  sections: MenuSection[],
  headerH: number,
  rowH: number
): FlatLayout {
  const entries: FlatEntry[] = [];
  const offsets: number[] = [];
  const sectionOffset: number[] = [];
  let y = 0;
  sections.forEach((section, sectionIndex) => {
    sectionOffset.push(y);
    entries.push({ type: "header", key: `h-${section.cateId}`, sectionIndex, cateName: section.cateName });
    offsets.push(y);
    y += headerH;
    for (const row of section.data) {
      entries.push({ type: "row", key: row.key, sectionIndex, row });
      offsets.push(y);
      y += rowH;
    }
  });
  return { entries, offsets, sectionOffset, totalHeight: y };
}

// Largest section index whose header offset <= y; clamps to [0, len-1];
// returns 0 for y before the first header.
export function sectionIndexForOffset(sectionOffset: number[], y: number): number {
  let idx = 0;
  for (let i = 0; i < sectionOffset.length; i++) {
    if (sectionOffset[i] <= y) idx = i;
    else break;
  }
  return idx;
}
